/**
 * App Storage class
 * @description This will be responsible for storing data into the application.
 * Commonly, people use LocalStorage or SessionStorage. This is just a wrapper over them
 * because to restrict the usage of Global window storage throughtout the application
 * Default, this is just using the LocalStorage
 */
export class AppStorage {
  storage: Storage;

  private listeners: Map<string, (value: any) => void> = new Map();

  constructor(storage?: Storage) {
    this.storage = storage || window.localStorage;

    if (!this.isSupported()) {
      throw new Error("Storage is not supported by browser!");
    }

    window.addEventListener("storage", (e: StorageEvent) => {
      if (e.key && this.listeners.has(e.key)) {
        try {
          const value = e.newValue ? JSON.parse(e.newValue) : null;
          this.listeners.get(e.key)!(value);
        } catch {
          this.listeners.get(e.key)!(null);
        }
      }
    });
  }

  /**
   * When another Browser tab (with same origin, of course) changes something in the Storage
   */
  public onChange<T>(key: string, cb: (value: T | null) => void) {
    this.listeners.set(key, cb);
  }

  public setItem<T = any>(key: string, value: T) {
    this.storage.setItem(key, JSON.stringify(value));
  }

  public getItem<T = any>(key: string): T | null {
    const item = this.storage.getItem(key);
    if (!item) {
      return null;
    }
    try {
      return JSON.parse(item) as T;
    } catch {
      // Treat corrupted or invalid JSON (e.g. stored "undefined") as absent
      return null;
    }
  }

  public removeItem(key: string) {
    this.storage.removeItem(key);
  }

  public removeItemsByPrefix(prefix: string) {
    // Iterate backwards: removing an item reindexes the remaining keys
    for (let keyIndex = this.storage.length - 1; keyIndex >= 0; keyIndex--) {
      const key = this.storage.key(keyIndex);
      if (key && key.startsWith(prefix)) {
        this.storage.removeItem(key);
      }
    }
  }

  public clear() {
    this.storage.clear();
  }

  /**
   * @description Check for storage support
   * @returns {boolean} supported
   * */
  private isSupported() {
    let supported = true;

    if (!this.storage) {
      supported = false;
    }

    return supported;
  }
}

/**
 * Creating the instance of storage. Default will be localStorage
 * but if you want to create instance for session storage then pass window.sessionStorage as parameter
 */
const appLocalStorage = new AppStorage();
const appSessionStorage = new AppStorage(window.sessionStorage);

export { appLocalStorage, appSessionStorage };
